// Fund settings sub-route — /fund/:fundId/settings (16.d.4.1).
// Extracted verbatim from the /fund mega-page (Fund.tsx ManagerView) as the
// first step of the 16.d.4 mega-page split. SettingsPanel self-loads via
// GET/PATCH /ops/funds/{id}/settings, so this page only needs fundId from the
// URL — no shared fund context required (the shared FundLayout + subnav lands
// in 16.d.4.5 once several sub-routes exist). Manager-gated in App.tsx.
//
// Three sections: Dealing / Management fee / Performance fee. The PATCH
// endpoint returns per-field `mutable: bool` flags — we disable the controls
// and surface the unlock condition in a hint. Single Save button PATCHes the
// diff vs. the loaded values; 409 error_kind maps to a per-field inline error.
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { PageHeader } from '../../components/shell';
import { api } from '../../lib/api';

export default function FundSettings() {
  const { fundId } = useParams<{ fundId: string }>();
  const [err, setErr] = useState<string | null>(null);
  if (!fundId) return null;
  return (
    <>
      <PageHeader
        kicker="FUND"
        title={<>Fund <em>settings.</em></>}
        meta={<a href="/admin/price-overrides" className="label-s" style={{ color: 'var(--ink-3)' }}>ASSET PRICE OVERRIDES →</a>}
      />
      {err && <p className="serif-body" style={{ color: 'var(--oxblood)', marginTop: 12 }}>{err}</p>}
      <SettingsPanel fundId={fundId} setErr={setErr} />
    </>
  );
}

type Settings = {
  id: string;
  name: string;
  base_currency: string;
  management_fee_annual_pct: string;
  performance_fee_pct: string;
  hurdle_kind: string;
  hurdle_annual_pct: string;
  crystallization_cadence: string;
  dealing_basis: string;
  dealing_tz: string;
  mutable: Record<string, boolean>;
};

const UNLOCK_HINT: Record<string, string> = {
  hurdle_kind: 'Crystallize the open period before changing the hurdle.',
  crystallization_cadence: 'Crystallize the open period before changing cadence.',
  dealing_basis: 'Refuses once subscriptions or redemptions exist on this fund.',
  dealing_tz: 'Refuses once any management or performance fee accrual exists.',
};

const ERROR_KIND_FIELD: Record<string, string> = {
  subs_reds_exist: 'dealing_basis',
  period_in_progress: 'crystallization_cadence',
  accruals_exist: 'hurdle_kind', // dealing_tz also uses this — UI shows it on whichever was edited
  transactions_exist: 'cost_basis_method',
};

const COMMON_TZS = ['Europe/Paris', 'America/New_York', 'Asia/Singapore', 'UTC'];

function SettingsPanel({ fundId, setErr }: { fundId: string; setErr: (s: string | null) => void }) {
  const [loaded, setLoaded] = useState<Settings | null>(null);
  const [draft, setDraft] = useState<Settings | null>(null);
  const [busy, setBusy] = useState(false);
  const [fieldErr, setFieldErr] = useState<{ field?: string; detail: string } | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    api<Settings>(`/ops/funds/${fundId}/settings`)
      .then(s => { setLoaded(s); setDraft(s); })
      .catch(() => setErr('Failed to load fund settings.'));
  }, [fundId]);

  if (!loaded || !draft) return null;

  const setField = <K extends keyof Settings>(k: K, v: Settings[K]) => {
    setDraft({ ...draft, [k]: v });
    setFieldErr(null);
    setToast(null);
  };

  const diff: Partial<Settings> = {};
  (Object.keys(loaded) as (keyof Settings)[]).forEach((k) => {
    if (k === 'mutable' || k === 'id' || k === 'name' || k === 'base_currency') return;
    if (draft[k] !== loaded[k]) (diff as any)[k] = draft[k];
  });
  const dirty = Object.keys(diff).length > 0;

  const save = async () => {
    setBusy(true); setErr(null); setFieldErr(null); setToast(null);
    try {
      const updated = await api<Settings>(`/ops/funds/${fundId}/settings`, {
        method: 'PATCH', body: diff,
      });
      setLoaded(updated); setDraft(updated);
      setToast('Saved.');
    } catch (e: any) {
      // The api helper throws Error with payload = response body.
      const payload = e?.payload || {};
      const detail = payload.detail || e?.message || 'Save failed.';
      const errorKind = payload.error_kind;
      if (errorKind && ERROR_KIND_FIELD[errorKind]) {
        setFieldErr({ field: ERROR_KIND_FIELD[errorKind], detail });
      } else {
        setFieldErr({ detail });
      }
    } finally {
      setBusy(false);
    }
  };

  const lockedHint = (field: string): string | undefined =>
    loaded.mutable[field] === false ? UNLOCK_HINT[field] : undefined;

  const fieldError = (field: string): string | undefined =>
    fieldErr?.field === field ? fieldErr.detail : undefined;

  return (
    <section style={{ marginTop: 24 }}>
      <div style={{ border: 'var(--hairline)', borderColor: 'var(--rule)' }}>
        {/* DEALING */}
        <SettingsSection title="DEALING">
          <Field
            label="DEALING BASIS"
            error={fieldError('dealing_basis')}
            hint={lockedHint('dealing_basis')}
          >
            <Radio
              name="dealing_basis"
              value={draft.dealing_basis}
              options={[
                ['prior_close', 'Prior close (t-1 NAV)'],
                ['at_instant', 'At instant (legacy)'],
              ]}
              disabled={!loaded.mutable.dealing_basis}
              onChange={v => setField('dealing_basis', v)}
            />
          </Field>
          <Field
            label="DEALING TIMEZONE"
            error={fieldError('dealing_tz')}
            hint={lockedHint('dealing_tz')}
          >
            <select
              className="field-input"
              value={COMMON_TZS.includes(draft.dealing_tz) ? draft.dealing_tz : '__custom'}
              disabled={!loaded.mutable.dealing_tz}
              onChange={e => {
                if (e.target.value !== '__custom') setField('dealing_tz', e.target.value);
              }}
              style={{ maxWidth: 240 }}
            >
              {COMMON_TZS.map(tz => <option key={tz} value={tz}>{tz}</option>)}
              <option value="__custom">Other IANA…</option>
            </select>
            {!COMMON_TZS.includes(draft.dealing_tz) && (
              <input
                className="field-input"
                value={draft.dealing_tz}
                disabled={!loaded.mutable.dealing_tz}
                onChange={e => setField('dealing_tz', e.target.value)}
                placeholder="e.g. Europe/London"
                style={{ marginTop: 6, maxWidth: 240 }}
              />
            )}
          </Field>
        </SettingsSection>

        {/* MANAGEMENT FEE */}
        <SettingsSection title="MANAGEMENT FEE">
          <Field label="ANNUAL %" error={fieldError('management_fee_annual_pct')}>
            <input
              className="field-input num"
              type="number"
              step="0.0001"
              min="0"
              max="100"
              value={draft.management_fee_annual_pct}
              onChange={e => setField('management_fee_annual_pct', e.target.value)}
              style={{ maxWidth: 140 }}
            />
          </Field>
        </SettingsSection>

        {/* PERFORMANCE FEE */}
        <SettingsSection title="PERFORMANCE FEE">
          <Field label="ANNUAL %" error={fieldError('performance_fee_pct')}>
            <input
              className="field-input num"
              type="number"
              step="0.0001"
              min="0"
              max="100"
              value={draft.performance_fee_pct}
              onChange={e => setField('performance_fee_pct', e.target.value)}
              style={{ maxWidth: 140 }}
            />
          </Field>
          <Field
            label="HURDLE"
            error={fieldError('hurdle_kind')}
            hint={lockedHint('hurdle_kind')}
          >
            <Radio
              name="hurdle_kind"
              value={draft.hurdle_kind}
              options={[
                ['none', 'None (HWM only)'],
                ['fixed_annual_pct', 'Fixed annual %'],
                ['index_btc', 'BTC index (not yet wired)'],
              ]}
              disabled={!loaded.mutable.hurdle_kind}
              onChange={v => setField('hurdle_kind', v)}
            />
          </Field>
          <Field label="HURDLE ANNUAL %" error={fieldError('hurdle_annual_pct')}>
            <input
              className="field-input num"
              type="number"
              step="0.0001"
              min="0"
              max="100"
              value={draft.hurdle_annual_pct}
              disabled={draft.hurdle_kind !== 'fixed_annual_pct'}
              onChange={e => setField('hurdle_annual_pct', e.target.value)}
              style={{ maxWidth: 140 }}
            />
          </Field>
          <Field
            label="CRYSTALLIZATION CADENCE"
            error={fieldError('crystallization_cadence')}
            hint={lockedHint('crystallization_cadence')}
          >
            <Radio
              name="crystallization_cadence"
              value={draft.crystallization_cadence}
              options={[
                ['annual', 'Annual'],
                ['quarterly', 'Quarterly'],
                ['monthly', 'Monthly'],
              ]}
              disabled={!loaded.mutable.crystallization_cadence}
              onChange={v => setField('crystallization_cadence', v)}
            />
          </Field>
        </SettingsSection>

        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
                      gap: 16, padding: '16px 20px', borderTop: 'var(--hairline)',
                      borderColor: 'var(--rule)' }}>
          {toast && <span className="label-s" style={{ color: 'var(--ink-3)' }}>{toast}</span>}
          {fieldErr && !fieldErr.field && (
            <span className="label-s" style={{ color: 'var(--oxblood)' }}>{fieldErr.detail}</span>
          )}
          <button
            className="btn"
            disabled={busy || !dirty}
            onClick={save}
          >Save</button>
        </div>
      </div>
    </section>
  );
}

function SettingsSection({ title, children }: { title: string; children: any }) {
  return (
    <div style={{ padding: '20px 20px 16px',
                  borderBottom: 'var(--hairline)', borderColor: 'var(--rule)' }}>
      <div className="label" style={{ marginBottom: 12 }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>{children}</div>
    </div>
  );
}

function Field({
  label, error, hint, children,
}: {
  label: string; error?: string; hint?: string; children: any;
}) {
  return (
    <div>
      <div className="label-s" style={{ marginBottom: 6 }}>{label}</div>
      {children}
      {error && (
        <div className="label-s" style={{ color: 'var(--oxblood)', marginTop: 6 }}>{error}</div>
      )}
      {!error && hint && (
        <div className="label-s" style={{ color: 'var(--ink-3)', marginTop: 6 }}>{hint}</div>
      )}
    </div>
  );
}

function Radio({
  name, value, options, disabled, onChange,
}: {
  name: string; value: string; options: [string, string][];
  disabled?: boolean; onChange: (v: string) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {options.map(([v, label]) => (
        <label key={v} className="serif-body" style={{ fontSize: 14, display: 'flex',
                                                       alignItems: 'center', gap: 8,
                                                       opacity: disabled ? 0.55 : 1 }}>
          <input
            type="radio"
            name={name}
            value={v}
            checked={value === v}
            disabled={disabled}
            onChange={e => onChange(e.target.value)}
          />
          {label}
        </label>
      ))}
    </div>
  );
}
